import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { ChatSession, ChatMessage } from '../../auth-config/types';
import { chatService } from '../../services/chatService';
import ChatMessageList, { type ChatMessageListHandle } from './ChatMessageList';
import ChatComposer from './ChatComposer';

interface Props {
  subjectId: number;
  standardId: number;
  subjectName?: string;
}

const ChatWidget: React.FC<Props> = ({ subjectId, standardId, subjectName }) => {
  // ── Visibility state ──────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ── Session & message state ───────────────────────────────────────────────
  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listRef = useRef<ChatMessageListHandle>(null);
  const initialised = useRef(false);

  // ── Load session & initial messages ──────────────────────────────────────
  const loadSession = useCallback(async () => {
    if (initialised.current) return;
    initialised.current = true;
    setIsLoadingHistory(true);
    setError(null);
    try {
      const sessions = await chatService.getSessions();
      // Find session for this subject (one-session-per-subject rule)
      const existing = sessions.find((s) => s.subjectId === subjectId) ?? null;
      setSession(existing);

      if (existing) {
        const paged = await chatService.getMessages(existing.id, 1, 20);
        setMessages(paged.messages);
        setPage(1);
        setHasMore(paged.hasMore);
        // Scroll to bottom after first render
        setTimeout(() => listRef.current?.scrollToBottom(), 50);
      }
    } catch (e: any) {
      setError('Could not load chat history. Try again.');
    } finally {
      setIsLoadingHistory(false);
    }
  }, [subjectId]);

  // Load when widget is first opened
  useEffect(() => {
    if (isOpen) loadSession();
  }, [isOpen, loadSession]);

  // The video-player navbar owns the collapsed chat launcher.
  useEffect(() => {
    const openChat = () => {
      console.log('Opening chat...');
      setIsOpen(true);
    };
    window.addEventListener('mm-chat-open-event', openChat);
    return () => window.removeEventListener('mm-chat-open-event', openChat);
  }, []);

  // Reset when subject changes (different video)
  useEffect(() => {
    initialised.current = false;
    setSession(null);
    setMessages([]);
    setPage(1);
    setHasMore(false);
    setError(null);
    if (isOpen) {
      loadSession();
    }
  }, [subjectId]);

  // ── Load older messages (pagination) ─────────────────────────────────────
  const handleLoadMore = useCallback(async () => {
    if (!session || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;
      const paged = await chatService.getMessages(session.id, nextPage, 20);
      // Prepend older messages
      setMessages((prev) => [...paged.messages, ...prev]);
      setPage(nextPage);
      setHasMore(paged.hasMore);
    } catch {
      // Silently fail — user can scroll up again
    } finally {
      setIsLoadingMore(false);
    }
  }, [session, page, hasMore, isLoadingMore]);

  // ── Send message ──────────────────────────────────────────────────────────
  const handleSend = useCallback(
    async (text: string) => {
      if (isSending) return;
      setIsSending(true);
      setError(null);

      // Optimistic user message
      const tempUserMsg: ChatMessage = {
        messageId: `temp-user-${Date.now()}`,
        sessionId: session?.id ?? '',
        role: 'user',
        content: text,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, tempUserMsg]);
      setTimeout(() => listRef.current?.scrollToBottom(), 30);

      try {
        const result = await chatService.sendMessage(subjectId, standardId, text);

        // Replace optimistic message + append AI reply
        setMessages((prev) => {
          const without = prev.filter((m) => m.messageId !== tempUserMsg.messageId);
          return [...without, result.userMessage, result.assistantMessage];
        });

        // Update session reference if it was newly created
        if (!session) {
          const updatedSessions = await chatService.getSessions();
          const newSession = updatedSessions.find((s) => s.subjectId === subjectId) ?? null;
          setSession(newSession);
        }
      } catch (e: any) {
        // Remove optimistic message on error
        setMessages((prev) => prev.filter((m) => m.messageId !== tempUserMsg.messageId));
        setError('Failed to send message. Please try again.');
      } finally {
        setIsSending(false);
        setTimeout(() => listRef.current?.scrollToBottom(), 50);
      }
    },
    [isSending, session, subjectId, standardId]
  );

  // ── FAB button (collapsed state) ──────────────────────────────────────────
  // ── Chat panel content (shared between floating & fullscreen) ─────────────
  const panelContent = (
    <div
      id="chat-widget-panel"
      className={`flex flex-col bg-white overflow-hidden ${
        isFullscreen
          ? 'fixed inset-0 z-[200] max-w-3xl mx-auto my-4 rounded-2xl shadow-2xl'
          : 'w-96 h-[540px] rounded-2xl shadow-2xl'
      }`}
      style={{ border: '1px solid rgba(0,0,0,0.08)' }}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{
          background: 'linear-gradient(135deg, #1e1b2e 0%, #2d1f5e 100%)',
        }}
      >
        <div className="flex items-center gap-2.5">
          {/* AI bot icon */}
          <div className="w-8 h-8 rounded-full bg-[#5624d0] flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
          </div>
          <div>
            <p className="text-white text-sm font-bold leading-none">MastiManch AI</p>
            <p className="text-purple-300 text-[11px] mt-0.5 leading-none truncate max-w-[180px]">
              {subjectName || 'Subject Assistant'}
            </p>
          </div>
        </div>

        {/* Header actions */}
        <div className="flex items-center gap-1">
          {/* Fullscreen toggle */}
          <button
            id="chat-widget-fullscreen-toggle"
            onClick={() => setIsFullscreen((f) => !f)}
            className="w-7 h-7 rounded-md flex items-center justify-center text-purple-300 hover:text-white hover:bg-white/10 transition"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              // Compress icon
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 9h4.5M15 9V4.5M15 9l5.25-5.25M9 15H4.5M9 15v4.5M9 15l-5.25 5.25M15 15h4.5M15 15v4.5M15 15l5.25 5.25" />
              </svg>
            ) : (
              // Expand icon
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </button>

          {/* Minimize */}
          <button
            id="chat-widget-minimize"
            onClick={() => { setIsOpen(false); setIsFullscreen(false); }}
            className="w-7 h-7 rounded-md flex items-center justify-center text-purple-300 hover:text-white hover:bg-white/10 transition"
            title="Minimize"
            aria-label="Minimize chat"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M18 12H6" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Loading history ── */}
      {isLoadingHistory && (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-gray-400">
            <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            <p className="text-xs">Loading conversation…</p>
          </div>
        </div>
      )}

      {/* ── Message list ── */}
      {!isLoadingHistory && (
        <ChatMessageList
          ref={listRef}
          messages={messages}
          isSending={isSending}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
        />
      )}

      {/* ── Error banner ── */}
      {error && (
        <div className="mx-3 mb-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600 flex items-center gap-2">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {/* ── Composer ── */}
      <ChatComposer onSend={handleSend} disabled={isSending || isLoadingHistory} />
    </div>
  );

  return (
    <>
      {/* Keyframe injection */}
      <style>{`
        @keyframes chatFabBounceIn {
          from { opacity: 0; transform: scale(0.4); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* Floating window */}
      {isOpen && !isFullscreen && (
        <div
          className="fixed bottom-6 right-6 z-[150]"
          style={{ animation: 'chatFabBounceIn 0.3s ease-out both' }}
        >
          {panelContent}
        </div>
      )}

      {/* Fullscreen overlay */}
      {isOpen && isFullscreen && (
        <div className="fixed inset-0 z-[180] bg-black/50 backdrop-blur-sm flex items-stretch justify-center p-4">
          <div className="flex-1 max-w-3xl flex flex-col">
            {panelContent}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
